import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DoctorLayout from "../../Layout/DoctorLayout";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { SkeletonBlock } from "../../components/Skeleton/Skeleton";
import { tokenManager } from "../../Api/tokenManager";
import { isAppointmentVisitWindowOver } from "../../utils/appointmentVisitWindow";
import "./DoctorAppointments.scss";

const fmtINR = (n) => `Rs ${Number(n || 0).toLocaleString("en-IN")}`;

function formatApptDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function pickPatientName(a) {
  const display = String(a?.patientDisplayName || "").trim();
  if (display) return display;
  if (a?.patientName) return String(a.patientName).trim();
  if (a?.patient?.name) return String(a.patient.name).trim();
  const p = a?.patientId;
  if (p && typeof p === "object" && p.name) return String(p.name).trim();
  return "";
}

function pickPatientAvatarUrl(a) {
  return String(a?.patientAvatarUrl || "").trim();
}

function pickServiceTitle(a) {
  const b = a?.bidLogId;
  if (b && typeof b === "object") {
    const sub = String(b.subCategoryName || "").trim();
    const cat = String(b.categoryName || "").trim();
    if (sub || cat) return [cat, sub].filter(Boolean).join(" · ");
  }
  return String(a?.subCategoryName || a?.subCategory?.name || "").trim();
}

function pickServiceSubtitle(a) {
  const slot = String(a?.timeSlot || "").trim();
  const when = formatApptDate(a?.appointmentDate);
  const clinic = String(a?.clinicName || "").trim();
  const bits = [slot || null, clinic || null, when || null].filter(Boolean);
  return bits.join(" · ");
}

function agreedOfferInr(a) {
  const b = a?.bidLogId;
  if (b && typeof b === "object") {
    const candidates = [b.reverseBidAmount, b.patientCounterAmount, b.patientBidPrice, b.affordabilityPrice];
    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  const top = Number(a?.offerAmount ?? a?.amount ?? a?.price);
  return Number.isFinite(top) && top > 0 ? top : null;
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "scheduled") return "doc_appt_status--scheduled";
  if (s === "completed") return "doc_appt_status--done";
  if (s === "pending_approval") return "doc_appt_status--pending";
  return "doc_appt_status--muted";
}

function formatStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending_approval") return "Pending approval";
  if (s === "scheduled") return "Scheduled";
  if (s === "completed") return "Completed";
  if (s === "cancelled" || s === "canceled") return "Cancelled";
  return status ? String(status).replace(/_/g, " ") : "—";
}
const BOOKED_STATUS_HINTS = [
  "booked",
  "confirmed",
  "scheduled",
  "appointment_confirmed",
  "upcoming",
  "completed",
];

/** API uses pending_approval for patient-submitted visits — must appear in this list. */
const EXPLICIT_SHOW_STATUSES = new Set(["pending_approval", "scheduled", "completed"]);

function isBookedAppointment(item) {
  const raw = String(item?.status || item?.appointmentStatus || item?.bookingStatus || item?.state || "")
    .trim()
    .toLowerCase();
  if (EXPLICIT_SHOW_STATUSES.has(raw)) return true;
  if (raw === "cancelled" || raw === "canceled") return false;
  return BOOKED_STATUS_HINTS.some((hint) => raw.includes(hint));
}

function normalizeAppointmentsPayload(res) {
  const body = res?.data;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.data?.items)) return body.data.items;
  if (Array.isArray(body)) return body;
  return [];
}

function pickBidLogId(a) {
  const b = a?.bidLogId;
  if (b && typeof b === "object" && b._id != null) return String(b._id);
  if (b != null) return String(b);
  return "";
}

function pickApptId(a) {
  if (a?._id != null) return String(a._id);
  if (a?.id != null) return String(a.id);
  return "";
}

function visitOutcomeRecorded(a) {
  const t = a?.visitOutcomeAt;
  return t != null && String(t).trim() !== "";
}

/** Accept / reschedule lives in bid chat until provider responds; admin then approves. */
function appointmentNextStep(a) {
  const st = String(a?.status || "").toLowerCase();
  if (st === "pending_approval") {
    const act = String(a?.doctorAction || "pending").toLowerCase();
    if (act === "pending") return "open_chat";
    return "waiting_admin";
  }
  if (st === "scheduled" && !visitOutcomeRecorded(a) && isAppointmentVisitWindowOver(a?.appointmentDate, a?.timeSlot)) {
    return "record_visit";
  }
  return null;
}

function visitOutcomeSummary(a) {
  if (!visitOutcomeRecorded(a)) return "";
  if (a.visitPatientAttended === false) return "No-show recorded";
  if (a.visitPatientAttended === true && a.visitProcedureProceeding === false) return "Attended · procedure not proceeding";
  if (a.visitPatientAttended === true && a.visitProcedureProceeding === true) {
    if (a.visitSamePriceAsBooked === true) return "Attended · same agreed price";
    if (a.visitSamePriceAsBooked === false && a.visitFinalAgreedPriceInr != null) {
      return `Attended · new price ${fmtINR(a.visitFinalAgreedPriceInr)}`;
    }
    return "Attended · visit logged";
  }
  return "Visit logged";
}

export default function DoctorAppointments() {
  const isHospitalFlow = tokenManager.getActiveProviderRole() === "hospital";
  const appointmentsPath = isHospitalFlow
    ? ENDPOINTS.HOSPITAL_APPOINTMENTS
    : ENDPOINTS.DOCTOR_APPOINTMENTS;
  const dashboardPath = isHospitalFlow ? "/hospital-dashboard" : "/doctor-dashboard";
  const confirmedPatientsPath = isHospitalFlow
    ? "/hospital-confirmed-patients"
    : "/doctor-confirmed-patients";
  const chatsPath = isHospitalFlow ? "/hospital-chats" : "/doctor-chats";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = (searchParams.get("search") || "").trim().toLowerCase();

  const [visitModal, setVisitModal] = useState(null);
  const [visitSubmitting, setVisitSubmitting] = useState(false);
  const [visitFormError, setVisitFormError] = useState("");
  const [visitForm, setVisitForm] = useState({
    patientAttended: null,
    procedureProceeding: null,
    sameAgreedPrice: null,
    finalAgreedPriceInr: "",
    priceChangeReason: "",
  });

  const openVisitModal = useCallback((a) => {
    setVisitForm({
      patientAttended: null,
      procedureProceeding: null,
      sameAgreedPrice: null,
      finalAgreedPriceInr: "",
      priceChangeReason: "",
    });
    setVisitFormError("");
    setVisitModal(a);
  }, []);

  const closeVisitModal = useCallback(() => {
    if (visitSubmitting) return;
    setVisitModal(null);
    setVisitFormError("");
  }, [visitSubmitting]);

  useEffect(() => {
    if (!visitModal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeVisitModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visitModal, closeVisitModal]);

  const loadAppointments = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    API.get(appointmentsPath, { params: { page: 1, limit: 100 } })
      .then((res) => {
        if (cancelled) return;
        setList(normalizeAppointmentsPayload(res));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || "Failed to load appointments");
          setList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentsPath]);

  useEffect(() => {
    return loadAppointments();
  }, [loadAppointments]);

  const submitVisitOutcome = useCallback(async () => {
    if (!visitModal) return;
    const id = pickApptId(visitModal);
    if (!id) {
      setVisitFormError("Missing appointment id.");
      return;
    }
    if (visitForm.patientAttended !== true && visitForm.patientAttended !== false) {
      setVisitFormError("Please indicate whether the patient attended.");
      return;
    }
    const body = { patientAttended: visitForm.patientAttended };
    if (visitForm.patientAttended) {
      if (visitForm.procedureProceeding !== true && visitForm.procedureProceeding !== false) {
        setVisitFormError("Please indicate whether the procedure is proceeding as planned.");
        return;
      }
      body.procedureProceeding = visitForm.procedureProceeding;
      if (visitForm.procedureProceeding) {
        if (visitForm.sameAgreedPrice !== true && visitForm.sameAgreedPrice !== false) {
          setVisitFormError("Please indicate if the agreed price is unchanged.");
          return;
        }
        body.sameAgreedPrice = visitForm.sameAgreedPrice;
        if (!visitForm.sameAgreedPrice) {
          const n = Number(String(visitForm.finalAgreedPriceInr).replace(/,/g, ""));
          if (!Number.isFinite(n) || n <= 0) {
            setVisitFormError("Enter the new agreed amount in rupees.");
            return;
          }
          body.finalAgreedPriceInr = Math.round(n);
          if (!String(visitForm.priceChangeReason || "").trim() || String(visitForm.priceChangeReason).trim().length < 8) {
            setVisitFormError("Explain why the price changed (at least 8 characters).");
            return;
          }
          body.priceChangeReason = String(visitForm.priceChangeReason).trim();
        }
      }
    }

    setVisitSubmitting(true);
    setVisitFormError("");
    try {
      await API.patch(`${appointmentsPath}/${id}/visit-outcome`, body);
      setVisitModal(null);
      loadAppointments();
    } catch (err) {
      setVisitFormError(err?.response?.data?.message || err?.message || "Could not save visit outcome.");
    } finally {
      setVisitSubmitting(false);
    }
  }, [appointmentsPath, visitModal, visitForm, loadAppointments]);

  const bookedList = useMemo(() => list.filter(isBookedAppointment), [list]);

  const bookedFiltered = useMemo(() => {
    if (!query) return bookedList;
    return bookedList.filter((a) => {
      const patientName = String(pickPatientName(a) || "").toLowerCase();
      const status = String(a?.status || a?.appointmentStatus || "").toLowerCase();
      const b = a?.bidLogId;
      const bidSub =
        b && typeof b === "object"
          ? String([b.categoryName, b.subCategoryName].filter(Boolean).join(" ") || "").toLowerCase()
          : "";
      const sub = String(a?.subCategoryName || a?.subCategory?.name || "").toLowerCase();
      const label = `${bidSub} ${sub}`.trim();
      return patientName.includes(query) || status.includes(query) || label.includes(query);
    });
  }, [bookedList, query]);

  const showPendingHint = useMemo(
    () => bookedFiltered.some((a) => appointmentNextStep(a) === "open_chat"),
    [bookedFiltered]
  );

  return (
    <DoctorLayout>
      <div className="doc_appt_page">
        {error && (loading || list.length > 0) ? <div className="doc_appt_error">{error}</div> : null}

        {!loading && showPendingHint ? (
          <p className="doc_appt_page_hint">
            To <strong>accept or reschedule</strong> a visit that is still pending your review, open{" "}
            <strong>Review in chat</strong> for that row — confirmation is not done on this table.
          </p>
        ) : null}

        {loading ? (
          <div className="doc_appt_table_wrap" aria-label="Loading appointments">
            <table className="doc_appt_table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>{"Service & visit"}</th>
                  <th>Agreed (Rs)</th>
                  <th>Status</th>
                  <th>Next step</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={`appt-skel-${idx}`}>
                    <td><SkeletonBlock height={14} width="70%" /></td>
                    <td><SkeletonBlock height={14} width="75%" /></td>
                    <td><SkeletonBlock height={14} width="45%" /></td>
                    <td><SkeletonBlock height={14} width="55%" /></td>
                    <td><SkeletonBlock height={14} width="60%" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : bookedFiltered.length === 0 ? (
          <div className="doc_appt_empty_wrap" role="status">
            <div className={`doc_appt_empty_card${error && list.length === 0 ? " doc_appt_empty_card--error" : ""}`}>
              <div className="doc_appt_empty_icon" aria-hidden="true">
                {error && list.length === 0 ? (
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9 12h6M9 16h4M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              {error && list.length === 0 ? (
                <>
                  <h2 className="doc_appt_empty_title">Could not load appointments</h2>
                  <p className="doc_appt_empty_text doc_appt_empty_text--error">{error}</p>
                  <div className="doc_appt_empty_actions">
                    <button type="button" className="doc_appt_empty_primary" onClick={() => loadAppointments()}>
                      Try again
                    </button>
                    <button type="button" className="doc_appt_empty_secondary" onClick={() => navigate(dashboardPath)}>
                      Back to dashboard
                    </button>
                  </div>
                </>
              ) : query ? (
                <>
                  <h2 className="doc_appt_empty_title">No matching records</h2>
                  <p className="doc_appt_empty_text">
                    Nothing in your appointments matches &ldquo;{searchParams.get("search")?.trim() || query}&rdquo;. Try a different name, procedure, or status, or clear the search.
                  </p>
                  <div className="doc_appt_empty_actions">
                    <button type="button" className="doc_appt_empty_primary" onClick={() => setSearchParams({}, { replace: true })}>
                      Clear search
                    </button>
                    <button type="button" className="doc_appt_empty_secondary" onClick={() => navigate(dashboardPath)}>
                      Back to dashboard
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="doc_appt_empty_title">No booked appointments yet</h2>
                  <p className="doc_appt_empty_text">
                    Booked/confirmed appointments will appear here with procedure and offer details. Check your dashboard and confirmed patients to move bids into booked status.
                  </p>
                  <div className="doc_appt_empty_actions">
                    <button type="button" className="doc_appt_empty_primary" onClick={() => navigate(dashboardPath)}>
                      Open dashboard
                    </button>
                    <button type="button" className="doc_appt_empty_secondary" onClick={() => navigate(confirmedPatientsPath)}>
                      Confirmed patients
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="doc_appt_table_wrap">
            <table className="doc_appt_table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>{"Service & visit"}</th>
                  <th>Agreed (Rs)</th>
                  <th>Status</th>
                  <th>Next step</th>
                </tr>
              </thead>
              <tbody>
                {bookedFiltered.map((a, idx) => {
                  const patient = pickPatientName(a);
                  const patientAvatar = pickPatientAvatarUrl(a);
                  const serviceTitle = pickServiceTitle(a);
                  const serviceSub = pickServiceSubtitle(a);
                  const offerN = agreedOfferInr(a);
                  const st = a?.status || "";
                  const bidId = pickBidLogId(a);
                  const step = appointmentNextStep(a);
                  return (
                    <tr key={a._id || a.id || idx}>
                      <td>
                        <div className="doc_appt_patient_cell">
                          {patientAvatar ? (
                            <img src={patientAvatar} alt="" className="doc_appt_patient_avatar_img" />
                          ) : (
                            <span className="doc_appt_patient_avatar_ph" aria-hidden />
                          )}
                          <div className="doc_appt_cell-primary">{patient || "—"}</div>
                        </div>
                      </td>
                      <td>
                        <div className="doc_appt_cell-primary">{serviceTitle || "Visit"}</div>
                        {serviceSub ? <div className="doc_appt_cell-muted">{serviceSub}</div> : null}
                      </td>
                      <td className="doc_appt_cell-num">{offerN != null ? fmtINR(offerN) : "—"}</td>
                      <td>
                        <span className={`doc_appt_status ${statusBadgeClass(st)}`}>{formatStatusLabel(st)}</span>
                      </td>
                      <td className="doc_appt_actions">
                        {step === "open_chat" && bidId ? (
                          <button
                            type="button"
                            className="doc_appt_row_btn"
                            onClick={() => navigate(`${chatsPath}?openBid=${encodeURIComponent(bidId)}`)}
                          >
                            Review in chat
                          </button>
                        ) : step === "waiting_admin" ? (
                          <span className="doc_appt_actions_muted">Awaiting HealBid admin</span>
                        ) : step === "record_visit" ? (
                          <button type="button" className="doc_appt_row_btn" onClick={() => openVisitModal(a)}>
                            Record visit outcome
                          </button>
                        ) : visitOutcomeRecorded(a) ? (
                          <span className="doc_appt_actions_summary" title={String(a.visitPriceChangeReason || "").trim() || undefined}>
                            {visitOutcomeSummary(a)}
                          </span>
                        ) : (
                          <span className="doc_appt_actions_muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {visitModal ? (
        <div className="doc_appt_modal_root" role="dialog" aria-modal="true" aria-labelledby="doc-appt-visit-title">
          <button type="button" className="doc_appt_modal_scrim" aria-label="Close" onClick={closeVisitModal} />
          <div className="doc_appt_modal_card">
            <div className="doc_appt_modal_head">
              <div className="doc_appt_modal_head_row">
                <h2 id="doc-appt-visit-title" className="doc_appt_modal_title">
                  Record visit outcome
                </h2>
                <button
                  type="button"
                  className="doc_appt_modal_close"
                  aria-label="Close dialog"
                  onClick={closeVisitModal}
                  disabled={visitSubmitting}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
              <p className="doc_appt_modal_lead">
                After the booked slot, log whether the patient attended, whether the procedure is proceeding, and if the
                agreed price changed. The patient will see this on their appointments.
              </p>
            </div>

            <div className="doc_appt_modal_body">
              <fieldset className="doc_appt_fieldset">
                <legend className="doc_appt_legend">Did the patient attend?</legend>
                <div className="doc_appt_radio_group">
                  <label className="doc_appt_radio doc_appt_radio--tile">
                    <input
                      type="radio"
                      name="patientAttended"
                      checked={visitForm.patientAttended === true}
                      onChange={() =>
                        setVisitForm((f) => ({
                          ...f,
                          patientAttended: true,
                          procedureProceeding: null,
                          sameAgreedPrice: null,
                          finalAgreedPriceInr: "",
                          priceChangeReason: "",
                        }))
                      }
                    />
                    <span className="doc_appt_radio__text">Yes</span>
                  </label>
                  <label className="doc_appt_radio doc_appt_radio--tile">
                    <input
                      type="radio"
                      name="patientAttended"
                      checked={visitForm.patientAttended === false}
                      onChange={() =>
                        setVisitForm((f) => ({
                          ...f,
                          patientAttended: false,
                          procedureProceeding: null,
                          sameAgreedPrice: null,
                          finalAgreedPriceInr: "",
                          priceChangeReason: "",
                        }))
                      }
                    />
                    <span className="doc_appt_radio__text">No (no-show)</span>
                  </label>
                </div>
              </fieldset>

              {visitForm.patientAttended === true ? (
                <fieldset className="doc_appt_fieldset">
                  <legend className="doc_appt_legend">Is the procedure / operation proceeding as planned?</legend>
                  <div className="doc_appt_radio_group">
                    <label className="doc_appt_radio doc_appt_radio--tile">
                      <input
                        type="radio"
                        name="procedureProceeding"
                        checked={visitForm.procedureProceeding === true}
                        onChange={() =>
                          setVisitForm((f) => ({
                            ...f,
                            procedureProceeding: true,
                            sameAgreedPrice: null,
                            finalAgreedPriceInr: "",
                            priceChangeReason: "",
                          }))
                        }
                      />
                      <span className="doc_appt_radio__text">Yes</span>
                    </label>
                    <label className="doc_appt_radio doc_appt_radio--tile">
                      <input
                        type="radio"
                        name="procedureProceeding"
                        checked={visitForm.procedureProceeding === false}
                        onChange={() =>
                          setVisitForm((f) => ({
                            ...f,
                            procedureProceeding: false,
                            sameAgreedPrice: null,
                            finalAgreedPriceInr: "",
                            priceChangeReason: "",
                          }))
                        }
                      />
                      <span className="doc_appt_radio__text">No</span>
                    </label>
                  </div>
                </fieldset>
              ) : null}

              {visitForm.patientAttended === true && visitForm.procedureProceeding === true ? (
                <fieldset className="doc_appt_fieldset">
                  <legend className="doc_appt_legend">Same agreed price as in the bid?</legend>
                  <div className="doc_appt_radio_group doc_appt_radio_group--stack-sm">
                    <label className="doc_appt_radio doc_appt_radio--tile">
                      <input
                        type="radio"
                        name="sameAgreedPrice"
                        checked={visitForm.sameAgreedPrice === true}
                        onChange={() =>
                          setVisitForm((f) => ({
                            ...f,
                            sameAgreedPrice: true,
                            finalAgreedPriceInr: "",
                            priceChangeReason: "",
                          }))
                        }
                      />
                      <span className="doc_appt_radio__text">Yes, unchanged</span>
                    </label>
                    <label className="doc_appt_radio doc_appt_radio--tile">
                      <input
                        type="radio"
                        name="sameAgreedPrice"
                        checked={visitForm.sameAgreedPrice === false}
                        onChange={() => setVisitForm((f) => ({ ...f, sameAgreedPrice: false }))}
                      />
                      <span className="doc_appt_radio__text">No, price changed</span>
                    </label>
                  </div>
                </fieldset>
              ) : null}

              {visitForm.patientAttended === true && visitForm.procedureProceeding === true && visitForm.sameAgreedPrice === false ? (
                <div className="doc_appt_modal_fields">
                  <label className="doc_appt_label">
                    New agreed amount (Rs)
                    <input
                      type="number"
                      className="doc_appt_input"
                      min={1}
                      step={1}
                      value={visitForm.finalAgreedPriceInr}
                      onChange={(e) => setVisitForm((f) => ({ ...f, finalAgreedPriceInr: e.target.value }))}
                      placeholder="e.g. 20000"
                    />
                  </label>
                  <label className="doc_appt_label">
                    Why did the price change?
                    <textarea
                      className="doc_appt_textarea"
                      rows={3}
                      value={visitForm.priceChangeReason}
                      onChange={(e) => setVisitForm((f) => ({ ...f, priceChangeReason: e.target.value }))}
                      placeholder="Short explanation for the patient (min. 8 characters)"
                    />
                  </label>
                </div>
              ) : null}
            </div>

            {visitFormError ? <p className="doc_appt_modal_error">{visitFormError}</p> : null}

            <div className="doc_appt_modal_actions">
              <button type="button" className="doc_appt_modal_secondary" onClick={closeVisitModal} disabled={visitSubmitting}>
                Cancel
              </button>
              <button type="button" className="doc_appt_modal_primary" onClick={submitVisitOutcome} disabled={visitSubmitting}>
                {visitSubmitting ? "Saving…" : "Save & notify patient"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </DoctorLayout>
  );
}

